<template>
  <!-- Match AssetsComponent (hideButtons mode): alphabet bar, loading, empty, card grid -->
  <div class="wiki-catalog-root">
    <div
      v-if="wikiError"
      class="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-4"
    >
      {{ wikiError }}
    </div>

    <div
      :key="`${alphabetKey}-${wikiRaw.length}`"
      class="bg-white rounded-lg shadow p-4 w-full mb-4"
    >
      <AlphabetFilterComponent
        :data="preLetterFiltered"
        filter-key="name"
        @filtered="handleLetterFilter"
      />
    </div>

    <div
      v-if="isLoading"
      class="flex flex-col items-center justify-center py-12 bg-white rounded-lg shadow"
    >
      <div class="text-center">
        <i class="pi pi-spin pi-spinner text-6xl text-blue-500 mb-4" />
        <h3 class="text-xl font-semibold text-gray-600 mb-2">
          Loading Assets...
        </h3>
        <p class="text-gray-500">
          Please wait while we fetch your data
        </p>
      </div>
    </div>

    <div
      v-else-if="filteredItems.length === 0"
      class="flex flex-col items-center justify-center py-12 bg-white rounded-lg shadow"
    >
      <div class="text-center">
        <i class="pi pi-box text-6xl text-gray-300 mb-4" />
        <h3 class="text-xl font-semibold text-gray-600 mb-2">
          No Assets Found
        </h3>
        <p class="text-gray-500 mb-4">
          Try adjusting search or filters
        </p>
        <Button
          label="Add Your First Asset"
          icon="pi pi-plus"
          class="p-button-primary"
          @click="router.push('/assets')"
        />
      </div>
    </div>

    <div
      v-else
      class="bg-white rounded-lg shadow p-4"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="item in filteredItems"
          :key="item.id"
          :class="[
            'bg-white',
            'border',
            'border-gray-200',
            'rounded-lg',
            'shadow-sm',
            'hover:shadow-md',
            'transition-shadow',
            'duration-300',
            'overflow-hidden',
            'cursor-pointer',
            'relative',
            categoryColor(item.displayCategory),
          ]"
          @click="goToWikiItem(item.id)"
        >
          <Button
            v-if="canWikiImport && !item.has_local_asset"
            v-tooltip.top="'Add to import basket'"
            type="button"
            class="!absolute top-2 right-2 z-10"
            icon="pi pi-plus"
            rounded
            severity="secondary"
            :aria-label="'Add to import basket'"
            @click.stop="onAddToImportBasket(item)"
          />
          <div class="h-40 relative flex items-center justify-center bg-gray-100">
            <AssetImage
              :data="item.apiData"
              :is-edit="false"
              :show-actions="false"
              :show-source-badge="false"
              size="small"
              :clickable="true"
            />
          </div>
          <div class="p-4">
            <h3 class="text-lg font-semibold text-gray-800 mb-2">
              {{ item.name }}
            </h3>
            <div class="flex items-center text-sm text-gray-600 mb-1">
              <i class="pi pi-tag mr-2" />
              <span class="truncate">{{ item.description || 'No description' }}</span>
            </div>
            <div class="flex items-center text-sm text-gray-600 mb-1">
              <i class="pi pi-building mr-2" />
              <span>{{ item.department }}</span>
            </div>
            <div class="flex items-center text-sm text-gray-600 mb-1">
              <i class="pi pi-shopping-cart mr-2" />
              <span>Type: {{ item.typeLabel }}</span>
            </div>
            <div class="flex items-center text-sm text-gray-600 mb-1">
              <i class="pi pi-check-circle mr-2" />
              <span>Catalog</span>
            </div>
            <div class="flex items-center text-sm text-gray-600 mb-1">
              <i class="pi pi-money-bill mr-2" />
              <span>—</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, defineProps, defineExpose } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import AlphabetFilterComponent from '@/components/shared/alphabetFilter/AlphabetFilterComponent.vue';
import AssetImage from '@/components/shared/AssetImage.vue';
import { getWikiItems, parseWikiSubAssetId, resolveWikiImageUrl } from '@/services/wikiService';
import { useAuthStore } from '@/stores/authStore';
import { useWikiImportBasketStore } from '@/stores/wikiImportBasketStore';

const props = defineProps({
  externalSearchTerm: {
    type: String,
    default: '',
  },
  filters: {
    type: Object,
    default: () => ({}),
  },
});

const router = useRouter();
const toast = useToast();
const authStore = useAuthStore();
const wikiImportBasket = useWikiImportBasketStore();

const canWikiImport = computed(
    () => authStore.isCompanyAdmin || authStore.isSystemAdmin
);

const wikiRaw = ref([]);
const isLoading = ref(true);
const wikiError = ref(null);
const alphabetKey = computed(
  () =>
    `${props.externalSearchTerm || ''}-${JSON.stringify(props.filters || {})}`
);

function humanizeAssetType(t) {
  if (!t) return '—';
  return String(t)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function wikiIdOrNull(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function extractClassificationIds(item) {
  const c = item.classification;
  const dc = item.default_classification;
  return {
    department_id: wikiIdOrNull(
      c?.department?.id ?? dc?.department_id ?? null
    ),
    master_category_id: wikiIdOrNull(
      c?.master_category?.id ?? dc?.master_category_id ?? null
    ),
    category_id: wikiIdOrNull(c?.category?.id ?? dc?.category_id ?? null),
  };
}

function mapApiItem(item) {
  const imageUrl = resolveWikiImageUrl(item.image_url) || null;
  const ids = extractClassificationIds(item);
  const catName = item.category || item.asset_type || '—';
  const displayCategory =
    item.classification?.category?.name || catName || 'Unknown';

  const rawSub =
    item.sub_asset_id != null && item.sub_asset_id !== ''
      ? Number(item.sub_asset_id)
      : parseWikiSubAssetId(item.id);
  const subAssetId = Number.isFinite(rawSub) ? rawSub : null;
  const suggestedCategoryId = wikiIdOrNull(
    item.classification?.category?.id ??
      item.default_classification?.category_id ??
      null
  );

  return {
    id: item.id,
    name: item.name,
    description: item.notes || catName,
    department: item.classification?.department?.name ?? '—',
    masterCategory: item.classification?.master_category?.name ?? null,
    displayCategory,
    typeLabel: humanizeAssetType(item.asset_type),
    asset_type: item.asset_type || null,
    master_asset_id: item.master_asset_id ?? null,
    sub_asset_id: subAssetId,
    has_local_asset: Boolean(item.has_local_asset),
    suggested_category_id: suggestedCategoryId,
    department_id: ids.department_id,
    master_category_id: ids.master_category_id,
    category_id: ids.category_id,
    apiData: {
      id: item.id,
      name: item.name,
      display_name: item.name,
      image_url: imageUrl,
    },
  };
}

function onAddToImportBasket(mapped) {
  if (mapped.has_local_asset) {
    return;
  }
  if (!mapped.master_asset_id || mapped.sub_asset_id == null) {
    toast.add({
      severity: 'warn',
      summary: 'Import basket',
      detail: 'This catalog row is missing master or sub-asset ids.',
      life: 5000,
    });

    return;
  }
  const added = wikiImportBasket.addFromWikiItem(mapped);
  if (added) {
    toast.add({
      severity: 'success',
      summary: 'Added to basket',
      detail: mapped.name,
      life: 2500,
    });
  } else {
    toast.add({
      severity: 'info',
      summary: 'Already in basket',
      detail: mapped.name,
      life: 3000,
    });
  }
}

const preLetterFiltered = computed(() => wikiRaw.value.map(mapApiItem));

const filteredItems = ref([]);

watch(
  preLetterFiltered,
  (list) => {
    filteredItems.value = list;
  },
  { immediate: true }
);

const handleLetterFilter = (filtered) => {
  filteredItems.value =
    filtered && filtered.length ? filtered : preLetterFiltered.value;
};

function wikiRequestParams() {
  const params = {};
  const f = props.filters || {};
  if (f.department_id != null && f.department_id !== '') {
    params.department_id = f.department_id;
  }
  if (f.master_category_id != null && f.master_category_id !== '') {
    params.master_category_id = f.master_category_id;
  }
  if (f.category_id != null && f.category_id !== '') {
    params.category_id = f.category_id;
  }
  const term = (props.externalSearchTerm || '').trim();
  if (term) {
    params.search = term;
  }
  return params;
}

async function fetchWikiItems() {
  isLoading.value = true;
  wikiError.value = null;
  try {
    const res = await getWikiItems(wikiRequestParams());
    wikiRaw.value = Array.isArray(res?.data) ? res.data : [];
  } catch (e) {
    wikiError.value =
      e?.response?.data?.message || e?.message || 'Failed to load catalog';
    wikiRaw.value = [];
  } finally {
    isLoading.value = false;
  }
}

let searchDebounceTimer = null;

// Use immediate:true so the first fetch fires as soon as the component mounts with
// whatever filters the parent already has — avoids a separate onMounted fetch and
// the "all-items flash" race condition when filters are pre-loaded from the URL.
watch(
  () => props.filters,
  () => {
    fetchWikiItems();
  },
  { deep: true, immediate: true }
);

watch(
  () => props.externalSearchTerm,
  (newVal, oldVal) => {
    if (newVal === oldVal) return;
    clearTimeout(searchDebounceTimer);
    searchDebounceTimer = setTimeout(() => {
      fetchWikiItems();
    }, 350);
  }
);

/**
 * Same pattern as AssetsComponent.reloadAssetsWithFilters — parent can force a fetch after
 * updating :filters so we do not rely only on prop watch timing vs EquipmentPage.
 */
function reloadWikiWithFilters() {
  fetchWikiItems();
}

const goToWikiItem = (id) => {
  router.push(`/wiki/item/${id}`);
};

function categoryColor(category) {
  switch (category) {
    case 'IT':
      return 'bg-blue-100';
    case 'HR':
      return 'bg-green-100';
    case 'Finance':
      return 'bg-yellow-100';
    default:
      return 'bg-gray-100';
  }
}

defineExpose({
  isLoading,
  fetchWikiItems,
  reloadWikiWithFilters,
});
</script>

<style scoped>
.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}
</style>
